import React, { useState, useEffect, useMemo } from 'react';
import { TabView, TabPanel } from 'primereact/tabview';
import { Card } from 'primereact/card';
import { RoutinePanel } from '../../../components/RoutinePanel/RoutinePanel';
import { useOrganizationContext } from '../../../context/OrganizationContext';
import './UserProfile.scss';
import { useParams } from 'react-router-dom';
import { ReservationPanel } from '../../../components/ReservationPanel/ReservationPanel';
import { useReservationTypes } from '../../../hooks/AppData/useReservationTypes';
import { ALL_FEATURES } from '../../../config/features';
import { AllowedFeatures } from '../../../types/features';
import { EditExternalUserModal } from '../../../components/EditExternalUserModal/EditExternalUserModal';
import { getCommonRestrictions } from '../../Reservations/Reservations';
import { BulkCancelReservationsDialog } from '../../../components/BulkCancelReservationsDialog/BulkCancelReservationsDialog';
import { ClientInfoCard } from './components/ClientInfoCard';
import { ReservationsTab } from './components/ReservationsTab';
import { RoutinesTab } from './components/RoutinesTab';
import { PaymentsTab } from './components/PaymentsTab';
import { useClientActions } from './hooks/useClientActions';
import { useExpensesCalendar } from './hooks/useExpensesCalendar';
import { useRoutinesCalendar } from './hooks/useRoutinesCalendar';
import { useProfileRoutinePanel } from './hooks/useProfileRoutinePanel';
import { useProfileClient } from './hooks/useProfileClient';
import { useReservationsCalendar } from './hooks/useReservationsCalendar';
import { useProfilePermissions } from './hooks/useProfilePermissions';
import { UserProfileSkeleton } from './components/UserProfileSkeleton';
import { ActivityView } from '../../../components/ActivityTimeline/ActivityViewToggle';

export const UserProfile = () => {
  const { organization, organizationLoading, orgBranches } = useOrganizationContext();
  const { externalUserId } = useParams();
  const { reservationTypeFromId, reservationTypeFeatures, reservationTypes, getMainReservationType } = useReservationTypes();

  const permissions = useProfilePermissions({ reservationTypes, reservationTypeFeatures });

  const reservationsCalendar = useReservationsCalendar({ organization, externalUserId });

  const { user, mutateUser, userLoading } = useProfileClient({ organization, externalUserId });

  const routinesCalendar = useRoutinesCalendar({ organization, externalUserId });

  const expensesCalendar = useExpensesCalendar({ organization, externalUserId });

  const [activeTabIndex, setActiveTabIndex] = useState<number | undefined>(undefined);
  // Calendar or list, chosen per tab. Kept here: the page shows its skeleton while a new month loads.
  const [activityViews, setActivityViews] = useState<Record<'reservations' | 'routines' | 'payments', ActivityView>>({
    reservations: 'calendar',
    routines: 'calendar',
    payments: 'calendar',
  });
  const activityView = (tab: keyof typeof activityViews) => ({
    view: activityViews[tab],
    onViewChange: (view: ActivityView) => setActivityViews((views) => ({ ...views, [tab]: view })),
  });

  const mainReservationType = useMemo(() => {
    return getMainReservationType();
  }, [getMainReservationType]);

  const calendarRestrictions = useMemo(() => {
    return getCommonRestrictions(reservationTypes, null, mainReservationType ?? null);
  }, [reservationTypes, mainReservationType]);

  useEffect(() => {
    if (permissions.hasAnyReservationTypeFeature) {
      setActiveTabIndex(0);
    } else {
      setActiveTabIndex(1);
    }
  }, [permissions.hasAnyReservationTypeFeature]);

  const routinePanel = useProfileRoutinePanel({ organization, externalUserId, user, setActiveTabIndex });

  const allowedRoutineFeatures: AllowedFeatures = {
    edit: [ALL_FEATURES.ROUTINES_WRITE],
    delete: [ALL_FEATURES.ROUTINES_WRITE],
    create: [ALL_FEATURES.ROUTINES_WRITE],
  };

  const clientActions = useClientActions({
    organization,
    externalUserId,
    user,
    reservations: reservationsCalendar.reservations,
    mutateUser,
    mutateReservations: reservationsCalendar.mutateReservations,
  });

  const [showEditModal, setShowEditModal] = useState(false);
  const [showBulkCancel, setShowBulkCancel] = useState(false);

  if (userLoading || reservationsCalendar.reservationsLoading || expensesCalendar.expensesLoading || organizationLoading) {
    return <UserProfileSkeleton />;
  }

  return (
    <div className='user-profile-page'>
      <ClientInfoCard
        user={user}
        organization={organization}
        externalUserId={externalUserId}
        permissions={permissions}
        mutateUser={mutateUser}
        openReservationPanel={reservationsCalendar.openReservationPanel}
        openRoutinePanel={routinePanel.openRoutinePanel}
        setShowEditModal={setShowEditModal}
        setShowBulkCancel={setShowBulkCancel}
        showToggleStatusDialog={clientActions.showToggleStatusDialog}
        isTogglingStatus={clientActions.isTogglingStatus}
      />

      {(permissions.hasAnyReservationTypeFeature || permissions.userHasViewRoutinesFeature) && (
        <Card className='user-profile-activity-card' title='Actividad'>
          <TabView activeIndex={activeTabIndex ?? 0} onTabChange={(e) => setActiveTabIndex(e.index)}>
            {permissions.hasAnyReservationTypeFeature && (
              <TabPanel header='Reservas'>
                <ReservationsTab
                  calendar={reservationsCalendar}
                  reservationTypeFromId={reservationTypeFromId}
                  externalUserId={externalUserId}
                  orgBranches={orgBranches}
                  {...activityView('reservations')}
                />
              </TabPanel>
            )}
            {permissions.userHasViewRoutinesFeature && (
              <TabPanel header='Rutinas'>
                <RoutinesTab
                  calendar={routinesCalendar}
                  user={user}
                  handleViewHistoryRoutine={routinePanel.handleViewHistoryRoutine}
                  {...activityView('routines')}
                />
              </TabPanel>
            )}
            {permissions.hasViewFisioReservationsFeature && (
              <TabPanel header='Pagos'>
                <PaymentsTab calendar={expensesCalendar} {...activityView('payments')} />
              </TabPanel>
            )}
          </TabView>
        </Card>
      )}

      <RoutinePanel
        resource={routinePanel.selectedRoutine}
        visible={routinePanel.routinePanelVisible}
        onHide={routinePanel.handleCloseRoutinePanel}
        onIsEditingChange={(value) => routinePanel.setEditingRoutine(value)}
        onSaveSuccess={mutateUser}
        isEditing={routinePanel.editingRoutine}
        isDeleting={false}
        overrideNextRoutine={routinePanel.overrideNextRoutine}
        creatingFromUserProfile
        allowedFeatures={allowedRoutineFeatures}
      />

      <ReservationPanel
        calendarRestrictions={calendarRestrictions}
        visible={reservationsCalendar.reservationPanelVisible}
        onHide={() => {
          reservationsCalendar.closeReservationPanel();
        }}
        onIsEditingChange={(value) => reservationsCalendar.setEditingReservation(value)}
        initialHours={undefined}
        clearInitialTemporaryEvent={() => {}}
        isEditing={reservationsCalendar.editingReservation}
        resource={reservationsCalendar.selectedReservation}
        isDeleting={false}
        externalUserProfileId={externalUserId}
        createModeReservationTypeId={reservationsCalendar.selectedReservation ? undefined : mainReservationType?.id}
        preselectedExternalUser={reservationsCalendar.selectedReservation ? undefined : user}
        mutateReservations={reservationsCalendar.mutateReservations}
      />

      {user && (
        <EditExternalUserModal
          visible={showEditModal}
          onHide={() => setShowEditModal(false)}
          user={user}
          onSave={clientActions.handleSaveUser}
          mutateReservations={reservationsCalendar.mutateReservations}
        />
      )}

      {externalUserId && (
        <BulkCancelReservationsDialog
          visible={showBulkCancel}
          onHide={() => setShowBulkCancel(false)}
          externalUserId={externalUserId}
          userName={`${user?.name ?? ''} ${user?.surnames ?? ''}`.trim()}
          onCancelled={clientActions.handleBulkCancelled}
        />
      )}
    </div>
  );
};
